import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { baseImgUrl } from '~/utils/constants';
import { MuiRemixLink } from '../shared/links';

type SwagCategory = {
  id?: number;
  product: string;
  productSrcSet?: string;
  url?: string;
  title: string;
  description: React.ReactNode;
  props?: React.CSSProperties;
};

function SwagCategoryComponent({ product, productSrcSet, title, description, props }: SwagCategory) {
  return (
    <Stack
      borderRadius={4}
      p={2}
      width="100%"
      sx={{
        ...props,
        backgroundColor: '#2a366a',
        backgroundImage: `url(${baseImgUrl}BG-Cajas.png)`,
        backgroundSize: 'cover',
        backgroundPosition: 'right',
        backgroundRepeat: 'no-repeat',
      }}
    >
      <Box
        bgcolor="#F2E8E3"
        width={80}
        height={80}
        borderRadius="50%"
        alignItems="center"
        justifyContent="center"
        mb={3}
      >
        <img
          src={product}
          srcSet={productSrcSet}
          alt={title}
          width={70}
          height={70}
          style={{
            width: '100%',
            height: 'auto',
          }}
        />
      </Box>

      <Box sx={{ mr: 4, height: 108, position: 'relative' }}>
        <Box
          sx={{
            pb: 2.5,
            height: '100%',
            overflow: 'auto',
            /* make scrollbar transparent */
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': {
              width: 0,
              background: 'transparent',
            },
          }}
        >
          <Typography variant="h6" fontWeight={700} fontSize="16px" color="#FFF" letterSpacing="0.15px">
            {title}
          </Typography>
          <Typography variant="body2" color="#FFF" letterSpacing="0.15px">
            {description}
          </Typography>
        </Box>
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: 30,
            backgroundImage: 'linear-gradient(to bottom, transparent, #2a366a)',
          }}
        />
      </Box>
    </Stack>
  );
}

export default function SwagForAll() {
  const swagCategories: SwagCategory[] = [
    {
      id: 1,
      product: `${baseImgUrl}swag-for-clients-engagement.png`,
      productSrcSet: `${baseImgUrl}swag-for-clients-engagement.png, ${baseImgUrl}swag-for-clients-engagement-2x.png 2x, ${baseImgUrl}swag-for-clients-engagement-3x.png 3x`,
      url: '/shop/swag-packs/loyalty',
      title: 'For Client Engagement',
      description: (
        <>
          Foster excitement for your brand and the value your team offers with exciting{' '}
          <b style={{ fontWeight: 600 }}>Swag for your clients</b>.
        </>
      ),
    },
    {
      id: 2,
      product: `${baseImgUrl}swag-for-employees-onboarding.png`,
      productSrcSet: `${baseImgUrl}swag-for-employees-onboarding.png, ${baseImgUrl}swag-for-employees-onboarding-2x.png 2x, ${baseImgUrl}swag-for-employees-onboarding-3x.png 3x`,
      url: '/shop/swag-packs/onboarding',
      title: 'For Employee Onboarding',
      description: (
        <>
          Help your new employees feel like they are becoming a part of something greater{' '}
          <b style={{ fontWeight: 600 }}>with custom Swag</b> that signifies they are joining an elite team.
        </>
      ),
    },
    {
      id: 3,
      product: `${baseImgUrl}swag-for-corporate-events.png`,
      productSrcSet: `${baseImgUrl}swag-for-corporate-events.png, ${baseImgUrl}swag-for-corporate-events-2x.png 2x, ${baseImgUrl}swag-for-corporate-events-3x.png 3x`,
      url: '/shop/swag-packs/events',
      title: 'For Corporate Events',
      description: (
        <>
          Networking and visibility are fundamental factors in your brand’s success. Give{' '}
          <b style={{ fontWeight: 600 }}>corporate event attendees</b> something to remember your name by with{' '}
          <b style={{ fontWeight: 600 }}>our high-quality Swag.</b>
        </>
      ),
    },
    {
      id: 4,
      product: `${baseImgUrl}swag-for-remote-teams.png`,
      productSrcSet: `${baseImgUrl}swag-for-remote-teams.png, ${baseImgUrl}swag-for-remote-teams-2x.png 2x, ${baseImgUrl}swag-for-remote-teams-3x.png 3x`,
      url: '/shop/swag-packs/corporate-swag',
      title: 'For Remote Teams',
      description:
        'For all the cutting-edge flexibility that remote work has to offer, it can be difficult to maintain the human connection. Remind your team what they are a part of with our custom Swag.',
    },
    {
      id: 5,
      product: `${baseImgUrl}swag-to-humanize-remote-events.png`,
      productSrcSet: `${baseImgUrl}swag-to-humanize-remote-events.png, ${baseImgUrl}swag-to-humanize-remote-events-2x.png 2x, ${baseImgUrl}swag-to-humanize-remote-events-3x.png 3x`,
      url: '/shop/swag-packs/events',
      title: 'For Remote Events',
      description: 'Our Swag solutions ensure your brand leaves a lasting impression no matter the venue.',
    },
    {
      id: 6,
      product: `${baseImgUrl}swag-for-funds-raising.png`,
      productSrcSet: `${baseImgUrl}swag-for-funds-raising.png, ${baseImgUrl}swag-for-funds-raising-2x.png 2x, ${baseImgUrl}swag-for-funds-raising-3x.png 3x`,
      url: '/shop/swag-packs/fundraising',
      title: 'For Fundraising',
      description: 'Exceptional quality Swag items means exceptional profits to fund the impact you want to have.',
    },
    {
      id: 7,
      product: `${baseImgUrl}swag-for-school-or-colleges.png`,
      productSrcSet: `${baseImgUrl}swag-for-school-or-colleges.png, ${baseImgUrl}swag-for-school-or-colleges-2x.png 2x, ${baseImgUrl}swag-for-school-or-colleges-3x.png 3x`,
      url: '/shop/swag-packs/college-and-schools',
      title: 'For Schools And Colleges',
      description: 'Embody school pride and spirit with custom Swag that shows the world what you’re made of.',
    },
    {
      id: 8,
      product: `${baseImgUrl}swag-for-in-person-events.png`,
      productSrcSet: `${baseImgUrl}swag-for-in-person-events.png, ${baseImgUrl}swag-for-in-person-events-2x.png 2x, ${baseImgUrl}swag-for-in-person-events-3x.png 3x`,
      url: '/shop/swag-packs/events',
      title: 'For In-person Events',
      description: 'Make any event memorable with exceptionally high-quality Swag for guests and attendees.',
    },
  ];

  return (
    <Box position="relative" py={10}>
      <Container>
        <Stack alignItems="center">
          <Typography variant="h4" mb={3} textAlign="center">
            Swag For All Occasions
          </Typography>

          <Typography
            variant="subtitle1"
            mb={{ xs: 4, sm: 12 }}
            width={{ xs: '100%', sm: '80%', md: '65%' }}
            mx="auto"
            textAlign="center"
          >
            You can never go wrong with sending company Swag. Sending company swag is an excellent way to have your
            remote teams feel more connected to the company. It can also be delivered to customers to improve
            engagement. Swag is a fun and easy way to show those involved with your company that you care about them.
          </Typography>
        </Stack>

        <Box
          sx={{
            height: '400px',
            width: '400px',
            position: 'absolute',
            top: '468px',
            right: 'calc(100% - 81px)',
            background: 'linear-gradient(180deg, #B7D8F3 0%, rgba(203, 255, 254, 0) 100%)',
            borderRadius: '50%',
            display: { xs: 'none', lg: 'block' },
          }}
        ></Box>

        <Box
          sx={{
            height: '600px',
            width: '510px',
            position: 'absolute',
            top: '-163px',
            left: 'calc(100% - 124px)',
            background: 'linear-gradient(180deg, #B7D8F3 0%, rgba(203, 255, 254, 0) 100%)',
            borderRadius: '50%',
            display: { xs: 'none', lg: 'block' },
          }}
        ></Box>

        <Grid container spacing={3} display={{ xs: 'none', sm: 'flex' }}>
          {swagCategories.map((category) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={category.id}>
              <MuiRemixLink href={category.url} key={category.id} underline="none" aria-label={category.url}>
                <SwagCategoryComponent
                  product={category.product}
                  productSrcSet={category.productSrcSet}
                  title={category.title}
                  description={category.description}
                  props={{ minWidth: 280, minHeight: 216 }}
                />
              </MuiRemixLink>
            </Grid>
          ))}
        </Grid>
      </Container>

      <Stack spacing={3} display={{ xs: 'flex', sm: 'none' }} overflow="auto" pb={2} px={2}>
        <Stack direction="row" flexWrap="nowrap" spacing={3} width="100%">
          {swagCategories.slice(0, 4).map((category) => (
            <MuiRemixLink href={category.url} key={category.id} underline="none" aria-label={category.url}>
              <SwagCategoryComponent
                product={category.product}
                productSrcSet={category.productSrcSet}
                title={category.title}
                description={category.description}
                props={{ minWidth: 280, minHeight: 216 }}
              />
            </MuiRemixLink>
          ))}
        </Stack>

        <Stack direction="row" flexWrap="nowrap" spacing={3} width="100%" pl={12}>
          {swagCategories.slice(4, 8).map((category) => (
            <MuiRemixLink href={category.url} key={category.id} underline="none" aria-label={category.url}>
              <SwagCategoryComponent
                product={category.product}
                productSrcSet={category.productSrcSet}
                title={category.title}
                description={category.description}
                props={{ minWidth: 280, minHeight: 216 }}
              />
            </MuiRemixLink>
          ))}
          <Box minWidth="1px" ml="16px !important"></Box>
        </Stack>
      </Stack>
    </Box>
  );
}
